import CopyIcon from "@atlaskit/icon/core/copy";
import {twJoin} from "tailwind-merge";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import type {EntityProfileSummaryField} from "~/components/entityProfile/types";

/**
 * Two-column label/value grid for the journey/customer Details tab (aside + page).
 * Scroll lives on the outer shell so the inner grid can size to content
 * (a height-constrained grid was collapsing field rows into thin strips).
 */
export function ProfileDetailsTabContent({
    fields,
    onCopyContact,
    className,
}: {
    fields: Array<EntityProfileSummaryField>;
    onCopyContact?: (value: string) => void;
    className?: string;
}) {
    if (fields.length === 0) {
        return (
            <div
                className={twJoin(
                    "h-full min-h-0 overflow-y-auto overscroll-contain p-ds-space-200 text-ds-text-subtle text-sm",
                    className,
                )}
            >
                No details available.
            </div>
        );
    }

    return (
        <div className={twJoin("h-full min-h-0 overflow-y-auto overscroll-contain", className)}>
            <div className="grid grid-cols-2 gap-x-ds-space-150 gap-y-ds-space-150 content-start p-ds-space-200">
                {fields.map((field) => (
                    <div key={field.id} className="min-w-0 grid gap-y-ds-space-025 content-start">
                        <span
                            className="text-[11px] leading-[14px] text-ds-text-subtlest truncate"
                            title={field.label}
                        >
                            {field.label}
                        </span>
                        <div className="flex items-start gap-x-ds-space-025 min-w-0">
                            <span
                                className="min-w-0 flex-1 text-sm leading-[20px] font-medium text-ds-text break-words"
                                title={field.value === "" || field.value === "-" ? undefined : field.value}
                            >
                                {field.value === "" ? "—" : field.value}
                            </span>
                            {field.copyValue !== undefined &&
                            field.copyValue !== "" &&
                            onCopyContact !== undefined ? (
                                <span className="shrink-0">
                                    <AnankeIconButton
                                        type="button"
                                        label="Copy"
                                        icon={(iconProps) => <CopyIcon {...iconProps} label="Copy" />}
                                        appearance="subtle"
                                        spacing="compact"
                                        onClick={() => {
                                            onCopyContact(field.copyValue ?? field.value);
                                        }}
                                    />
                                </span>
                            ) : null}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
